import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { ServerSearchSelect } from '@/components/ui/ServerSearchSelect';
import { estagioService, alunoService, empresaService, instituicaoService, seguradoraService, cursoService, agenteIntegradorService } from '@/services/api';
import { AgenteIntegrador, Aluno, Curso, Empresa, Estagio, Instituicao, Seguradora } from '@/types';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useQueryInvalidation } from '@/hooks/useQueryInvalidation';
import { minutosParaHorasMinutos, horasMinutosParaMinutos, formatarInputHorasMinutos } from '@/utils/timeConverter';

const estagioSchema = z.object({
  alunoId: z.number().min(1, 'Aluno é obrigatório'),
  empresaId: z.number().min(1, 'Empresa é obrigatória'),
  instituicaoId: z.number().min(1, 'Instituição é obrigatória'),
  cursoId: z.union([z.number(), z.string().transform(val => val === '' ? undefined : Number(val))]).optional(),
  tipo: z.enum(['Obrigatorio', 'NaoObrigatorio'], {
    required_error: 'Tipo é obrigatório',
  }),
  remunerado: z.boolean(),
  origemInstituicao: z.string().transform(val => val === '' ? undefined : val).optional(),
  dataInicio: z.string().min(1, 'Data de início é obrigatória'),
  dataTermino: z.string().min(1, 'Data de término é obrigatória'),
  cargaHorariaSemanal: z.string()
    .regex(/^\d{1,3}:\d{2}$/, 'Formato deve ser HH:mm (ex: 20:00)')
    .refine((val) => {
      if (!val) return true;
      const minutos = horasMinutosParaMinutos(val);
      return minutos <= 2640;
    }, 'Carga horária semanal não pode ser superior a 44 horas')
    .optional(),
  bolsaAuxilio: z.number().optional(),
  auxilioTransporte: z.number().optional(),
  valeAlimentacao: z.preprocess(
    (val) => (val === '' || val === null || val === undefined || Number.isNaN(val) ? undefined : val),
    z.number().optional()
  ),
  transporteFornecidoEmpresa: z.boolean().default(false),
  seguroApolice: z.string().transform(val => val === '' ? undefined : val).optional(),
  dataEmissao: z.string().transform(val => val === '' ? undefined : val).optional(),
  seguradoraId: z.number().optional(),
  agenteIntegradorId: z.number().optional(),
  status: z.enum(['Ativo', 'Cancelado', 'Concluido'], {
    required_error: 'Status é obrigatório',
  }),
  dataAssinatura: z.string().transform(val => val === '' ? undefined : val).optional(),
  dataCancelamento: z.string().transform(val => val === '' ? undefined : val).optional(),
  motivoEncerramentoId: z.number().optional(),
  possuiResponsavelMenor: z.boolean(),
});

type EstagioFormData = Omit<Estagio, 'id' | 'createdAt' | 'updatedAt' | 'aluno' | 'empresa' | 'instituicao' | 'seguradora' | 'agenteIntegrador' | 'curso' | 'horarios' | 'relatorios' | 'cargaHorariaSemanal'> & {
  cargaHorariaSemanal?: string;
};

export const NovaEstagioPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!id;
  const { handleError, handleSuccess } = useErrorHandler();
  const { invalidateEstagios } = useQueryInvalidation();

  const [alunoLabel, setAlunoLabel] = useState('');
  const [empresaLabel, setEmpresaLabel] = useState('');
  const [instituicaoLabel, setInstituicaoLabel] = useState('');
  const [cursoLabel, setCursoLabel] = useState('');
  const [seguradoraLabel, setSeguradoraLabel] = useState('');
  const [agenteIntegradorLabel, setAgenteIntegradorLabel] = useState('');
  const [cargaHorariaValue, setCargaHorariaValue] = useState<string>('00:00');

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<EstagioFormData>({
    resolver: zodResolver(estagioSchema),
    defaultValues: {
      remunerado: false,
      possuiResponsavelMenor: false,
      transporteFornecidoEmpresa: false,
      status: 'Ativo',
      tipo: 'Obrigatorio',
    },
  });

  const transporteFornecidoEmpresa = watch('transporteFornecidoEmpresa');
  const alunoId = watch('alunoId');
  const empresaId = watch('empresaId');
  const instituicaoId = watch('instituicaoId');
  const cursoId = watch('cursoId');
  const seguradoraId = watch('seguradoraId');
  const agenteIntegradorId = watch('agenteIntegradorId');

  const { data: estagioData, isLoading: loadingEstagio } = useQuery({
    queryKey: ['estagio', id],
    queryFn: () => estagioService.getById(Number(id)),
    enabled: isEditing,
  });

  const createEstagioMutation = useMutation({
    mutationFn: estagioService.create,
    onSuccess: () => {
      invalidateEstagios();
      handleSuccess('Estágio criado com sucesso!');
      navigate('/estagios');
    },
    onError: (error: unknown) => {
      handleError(error as Error, 'Erro ao criar estágio');
    },
  });

  const updateEstagioMutation = useMutation({
    mutationFn: ({ id: estagioId, data }: { id: number; data: Record<string, unknown> }) =>
      estagioService.update(estagioId, data),
    onSuccess: () => {
      invalidateEstagios(id);
      handleSuccess('Estágio atualizado com sucesso!');
      navigate('/estagios');
    },
    onError: (error: unknown) => {
      handleError(error as Error, 'Erro ao atualizar estágio');
    },
  });

  useEffect(() => {
    if (!estagioData) return;
    setValue('alunoId', estagioData.alunoId);
    setValue('empresaId', estagioData.empresaId);
    setValue('instituicaoId', estagioData.instituicaoId);
    setValue('cursoId', estagioData.cursoId);
    setValue('tipo', estagioData.tipo);
    setValue('remunerado', estagioData.remunerado);
    setValue('origemInstituicao', estagioData.origemInstituicao || '');
    setValue('dataInicio', estagioData.dataInicio.split('T')[0]);
    setValue('dataTermino', estagioData.dataTermino.split('T')[0]);
    const cargaHorariaFormatada = minutosParaHorasMinutos(estagioData.cargaHorariaSemanal);
    setCargaHorariaValue(cargaHorariaFormatada);
    setValue('cargaHorariaSemanal', cargaHorariaFormatada);
    setValue('bolsaAuxilio', estagioData.bolsaAuxilio || undefined);
    setValue('auxilioTransporte', estagioData.auxilioTransporte || undefined);
    setValue('valeAlimentacao', estagioData.valeAlimentacao || undefined);
    setValue('transporteFornecidoEmpresa', estagioData.transporteFornecidoEmpresa ?? false);
    setValue('seguroApolice', estagioData.seguroApolice || '');
    setValue('dataEmissao', estagioData.dataEmissao?.split('T')[0] || '');
    setValue('seguradoraId', estagioData.seguradoraId || undefined);
    setValue('agenteIntegradorId', estagioData.agenteIntegradorId || undefined);
    setValue('status', estagioData.status);
    setValue('dataAssinatura', estagioData.dataAssinatura?.split('T')[0] || '');
    setValue('dataCancelamento', estagioData.dataCancelamento?.split('T')[0] || '');
    setValue('motivoEncerramentoId', estagioData.motivoEncerramentoId || undefined);
    setValue('possuiResponsavelMenor', estagioData.possuiResponsavelMenor);
    setAlunoLabel(estagioData.aluno?.nome ?? '');
    setEmpresaLabel(estagioData.empresa?.nomeFantasia ?? '');
    setInstituicaoLabel(estagioData.instituicao?.nome ?? '');
    setCursoLabel(
      estagioData.curso
        ? `${estagioData.curso.nome}${estagioData.curso.nivel ? ` (${estagioData.curso.nivel})` : ''}`
        : ''
    );
    setSeguradoraLabel(estagioData.seguradora?.nome ?? '');
    setAgenteIntegradorLabel(estagioData.agenteIntegrador?.nome ?? '');
  }, [estagioData, setValue]);

  const onSubmit = async (data: EstagioFormData) => {
    try {
      setIsSubmitting(true);
      const formData = {
        ...data,
        dataInicio: new Date(data.dataInicio).toISOString(),
        dataTermino: new Date(data.dataTermino).toISOString(),
        dataAssinatura: data.dataAssinatura ? new Date(data.dataAssinatura).toISOString() : undefined,
        dataCancelamento: data.dataCancelamento ? new Date(data.dataCancelamento).toISOString() : undefined,
        dataEmissao: data.dataEmissao ? new Date(data.dataEmissao).toISOString() : undefined,
        cursoId: data.cursoId || undefined,
        cargaHorariaSemanal: data.cargaHorariaSemanal ? horasMinutosParaMinutos(data.cargaHorariaSemanal) : undefined,
        bolsaAuxilio: data.bolsaAuxilio ?? 0,
        auxilioTransporte: data.transporteFornecidoEmpresa ? 0 : (data.auxilioTransporte ?? 0),
        valeAlimentacao:
          data.valeAlimentacao != null && !Number.isNaN(data.valeAlimentacao)
            ? data.valeAlimentacao
            : isEditing
              ? null
              : undefined,
        transporteFornecidoEmpresa: data.transporteFornecidoEmpresa ?? false,
        seguroApolice: data.seguroApolice?.trim() || undefined,
        origemInstituicao: data.origemInstituicao?.trim() || undefined,
        agenteIntegradorId: data.agenteIntegradorId || undefined,
      };
      if (isEditing) {
        await updateEstagioMutation.mutateAsync({ id: Number(id), data: formData as Record<string, unknown> });
      } else {
        await createEstagioMutation.mutateAsync(formData as never);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadingEstagio) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-gray-500">Carregando dados...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
          {isEditing ? 'Editar Estágio' : 'Novo Estágio'}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {isEditing ? 'Edite as informações do estágio' : 'Cadastre um novo estágio no sistema'}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Dados Básicos</CardTitle>
            <CardDescription>Informações essenciais do estágio</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="alunoId">Aluno *</Label>
                <ServerSearchSelect<Aluno>
                  id="alunoId"
                  value={alunoId || null}
                  selectedLabel={alunoLabel}
                  onChange={(selectedId, item) => {
                    setValue('alunoId', selectedId ?? 0, { shouldValidate: true });
                    setAlunoLabel(item ? `${item.nome}${item.cpf ? ` (${item.cpf})` : ''}` : '');
                  }}
                  fetchPage={(params) => alunoService.list(params)}
                  fetchById={(selectedId) => alunoService.getById(selectedId)}
                  getOptionLabel={(a) => `${a.nome}${a.cpf ? ` (${a.cpf})` : ''}`}
                  queryKey="alunos-select"
                  placeholder="Buscar aluno…"
                  error={Boolean(errors.alunoId)}
                />
                {errors.alunoId && <p className="mt-1 text-sm text-error-600">{errors.alunoId.message}</p>}
              </div>
              <div>
                <Label htmlFor="empresaId">Empresa *</Label>
                <ServerSearchSelect<Empresa>
                  id="empresaId"
                  value={empresaId || null}
                  selectedLabel={empresaLabel}
                  onChange={(selectedId, item) => {
                    setValue('empresaId', selectedId ?? 0, { shouldValidate: true });
                    setEmpresaLabel(item?.nomeFantasia ?? '');
                  }}
                  fetchPage={(params) => empresaService.list(params)}
                  fetchById={(selectedId) => empresaService.getById(selectedId)}
                  getOptionLabel={(e) => e.nomeFantasia}
                  queryKey="empresas-select"
                  placeholder="Buscar empresa…"
                  error={Boolean(errors.empresaId)}
                />
                {errors.empresaId && <p className="mt-1 text-sm text-error-600">{errors.empresaId.message}</p>}
              </div>
              <div>
                <Label htmlFor="instituicaoId">Instituição *</Label>
                <ServerSearchSelect<Instituicao>
                  id="instituicaoId"
                  value={instituicaoId || null}
                  selectedLabel={instituicaoLabel}
                  onChange={(selectedId, item) => {
                    setValue('instituicaoId', selectedId ?? 0, { shouldValidate: true });
                    setInstituicaoLabel(item?.nome ?? '');
                  }}
                  fetchPage={(params) => instituicaoService.list(params)}
                  fetchById={(selectedId) => instituicaoService.getById(selectedId)}
                  getOptionLabel={(i) => i.nome}
                  queryKey="instituicoes-select"
                  placeholder="Buscar instituição…"
                  error={Boolean(errors.instituicaoId)}
                />
                {errors.instituicaoId && <p className="mt-1 text-sm text-error-600">{errors.instituicaoId.message}</p>}
              </div>
              <div>
                <Label htmlFor="cursoId">Curso</Label>
                <ServerSearchSelect<Curso>
                  id="cursoId"
                  value={typeof cursoId === 'number' ? cursoId : null}
                  selectedLabel={cursoLabel}
                  onChange={(selectedId, item) => {
                    setValue('cursoId', selectedId ?? undefined, { shouldValidate: true });
                    setCursoLabel(item ? `${item.nome}${item.nivel ? ` (${item.nivel})` : ''}` : '');
                  }}
                  fetchPage={(params) => cursoService.list(params)}
                  fetchById={(selectedId) => cursoService.getById(selectedId)}
                  getOptionLabel={(c) => `${c.nome}${c.nivel ? ` (${c.nivel})` : ''}`}
                  queryKey="cursos-select"
                  placeholder="Buscar curso…"
                />
              </div>

              <div>
                <Label htmlFor="tipo">Tipo *</Label>
                <select
                  id="tipo"
                  {...register('tipo')}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.tipo ? 'border-error-500' : 'border-gray-300'
                    }`}
                >
                  <option value="Obrigatorio">Obrigatório</option>
                  <option value="NaoObrigatorio">Não Obrigatório</option>
                </select>
                {errors.tipo && (
                  <p className="mt-1 text-sm text-error-600">{errors.tipo.message}</p>
                )}
              </div>

              {/* <div>
                <Label htmlFor="status">Status *</Label>
                <select
                  id="status"
                  {...register('status')}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.status ? 'border-error-500' : 'border-gray-300'
                    }`}
                >
                  <option value="Ativo">Ativo</option>
                  <option value="Cancelado">Cancelado</option>
                  <option value="Concluido">Concluído</option>
                </select>
                {errors.status && (
                  <p className="mt-1 text-sm text-error-600">{errors.status.message}</p>
                )}
              </div> */}

              <div>
                <Label htmlFor="origemInstituicao">Origem da Instituição</Label>
                <Input
                  id="origemInstituicao"
                  placeholder="Ex: Curso de Ciência da Computação"
                  {...register('origemInstituicao')}
                  className={errors.origemInstituicao ? 'border-error-500' : ''}
                />
                {errors.origemInstituicao && (
                  <p className="mt-1 text-sm text-error-600">{errors.origemInstituicao.message}</p>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="remunerado"
                  {...register('remunerado')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <Label htmlFor="remunerado" className="ml-2">
                  Estágio remunerado
                </Label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="possuiResponsavelMenor"
                  {...register('possuiResponsavelMenor')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <Label htmlFor="possuiResponsavelMenor" className="ml-2">
                  Possui responsável
                </Label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Datas */}
        <Card>
          <CardHeader>
            <CardTitle>Datas</CardTitle>
            <CardDescription>
              Período e datas importantes do estágio
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="dataInicio">Data de Início *</Label>
                <Input
                  id="dataInicio"
                  type="date"
                  {...register('dataInicio')}
                  className={errors.dataInicio ? 'border-error-500' : ''}
                />
                {errors.dataInicio && (
                  <p className="mt-1 text-sm text-error-600">{errors.dataInicio.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="dataTermino">Data de Término *</Label>
                <Input
                  id="dataTermino"
                  type="date"
                  {...register('dataTermino')}
                  className={errors.dataTermino ? 'border-error-500' : ''}
                />
                {errors.dataTermino && (
                  <p className="mt-1 text-sm text-error-600">{errors.dataTermino.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="dataAssinatura">Data de Assinatura</Label>
                <Input
                  id="dataAssinatura"
                  type="date"
                  {...register('dataAssinatura')}
                  className={errors.dataAssinatura ? 'border-error-500' : ''}
                />
                {errors.dataAssinatura && (
                  <p className="mt-1 text-sm text-error-600">{errors.dataAssinatura.message}</p>
                )}
              </div>

                <div>
                <Label htmlFor="dataCancelamento">Data de Cancelamento</Label>
                <Input
                  id="dataCancelamento"
                  type="date"
                  {...register('dataCancelamento')}
                  className={errors.dataCancelamento ? 'border-error-500' : ''}
                  disabled
                  readOnly
                />
                {errors.dataCancelamento && (
                  <p className="mt-1 text-sm text-error-600">{errors.dataCancelamento.message}</p>
                )}
                </div>
            </div>
          </CardContent>
        </Card>

        {/* Detalhes Adicionais */}
        <Card>
          <CardHeader>
            <CardTitle>Detalhes Adicionais</CardTitle>
            <CardDescription>
              Informações complementares do estágio
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cargaHorariaSemanal">Carga Horária Semanal (HH:mm)</Label>
                <Input
                  id="cargaHorariaSemanal"
                  type="text"
                  placeholder="Ex: 20:00"
                  value={cargaHorariaValue}
                  onChange={(e) => {
                    const formatted = formatarInputHorasMinutos(e.target.value);
                    setCargaHorariaValue(formatted);
                    setValue('cargaHorariaSemanal', formatted);
                  }}
                  className={errors.cargaHorariaSemanal ? 'border-error-500' : ''}
                  maxLength={6}
                />
                {errors.cargaHorariaSemanal && (
                  <p className="mt-1 text-sm text-error-600">{errors.cargaHorariaSemanal.message}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">Formato: HH:mm (ex: 20:00 = 20 horas) - Máximo: 44:00</p>
              </div>

              <div>
                <Label htmlFor="bolsaAuxilio">Valor da bolsa (R$)</Label>
                <Input
                  id="bolsaAuxilio"
                  type="number"
                  step="0.01"
                  placeholder="Ex: 800.00"
                  {...register('bolsaAuxilio', { valueAsNumber: true })}
                  className={errors.bolsaAuxilio ? 'border-error-500' : ''}
                />
                {errors.bolsaAuxilio && (
                  <p className="mt-1 text-sm text-error-600">{errors.bolsaAuxilio.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="auxilioTransporte">Auxílio-transporte (R$)</Label>
                <Input
                  id="auxilioTransporte"
                  type="number"
                  step="0.01"
                  placeholder="Ex: 200.00"
                  disabled={transporteFornecidoEmpresa}
                  {...register('auxilioTransporte', { valueAsNumber: true })}
                  className={errors.auxilioTransporte ? 'border-error-500' : ''}
                />
                {errors.auxilioTransporte && (
                  <p className="mt-1 text-sm text-error-600">{errors.auxilioTransporte.message}</p>
                )}
                <div className="flex items-center mt-2">
                  <input
                    type="checkbox"
                    id="transporteFornecidoEmpresa"
                    {...register('transporteFornecidoEmpresa', {
                      onChange: (e) => {
                        if (e.target.checked) {
                          setValue('auxilioTransporte', undefined);
                        }
                      },
                    })}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <Label htmlFor="transporteFornecidoEmpresa" className="ml-2">
                    Transporte fornecido pela empresa
                  </Label>
                </div>
              </div>

              <div>
                <Label htmlFor="valeAlimentacao">Vale alimentação (R$) <span className="text-gray-400 font-normal">(opcional)</span></Label>
                <Input
                  id="valeAlimentacao"
                  type="number"
                  step="0.01"
                  placeholder="Ex: 150.00"
                  {...register('valeAlimentacao', { valueAsNumber: true })}
                  className={errors.valeAlimentacao ? 'border-error-500' : ''}
                />
                {errors.valeAlimentacao && (
                  <p className="mt-1 text-sm text-error-600">{errors.valeAlimentacao.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="seguroApolice">Apólice do Seguro</Label>
                <Input
                  id="seguroApolice"
                  placeholder="Ex: AP123456"
                  {...register('seguroApolice')}
                  className={errors.seguroApolice ? 'border-error-500' : ''}
                />
                {errors.seguroApolice && (
                  <p className="mt-1 text-sm text-error-600">{errors.seguroApolice.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="dataEmissao">Data de emissão</Label>
                <Input
                  type="date"
                  id="dataEmissao"
                  {...register('dataEmissao')}
                  className={errors.dataEmissao ? 'border-error-500' : ''}
                />
                {errors.dataEmissao && (
                  <p className="mt-1 text-sm text-error-600">{errors.dataEmissao.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="seguradoraId">Seguradora</Label>
                <ServerSearchSelect<Seguradora>
                  id="seguradoraId"
                  value={seguradoraId || null}
                  selectedLabel={seguradoraLabel}
                  onChange={(selectedId, item) => {
                    setValue('seguradoraId', selectedId ?? undefined, { shouldValidate: true });
                    setSeguradoraLabel(item?.nome ?? '');
                  }}
                  fetchPage={(params) => seguradoraService.list(params)}
                  fetchById={(selectedId) => seguradoraService.getById(selectedId)}
                  getOptionLabel={(s) => s.nome}
                  queryKey="seguradoras-select"
                  placeholder="Buscar seguradora…"
                />
              </div>
              <div>
                <Label htmlFor="agenteIntegradorId">Agente Integrador</Label>
                <ServerSearchSelect<AgenteIntegrador>
                  id="agenteIntegradorId"
                  value={agenteIntegradorId || null}
                  selectedLabel={agenteIntegradorLabel}
                  onChange={(selectedId, item) => {
                    setValue('agenteIntegradorId', selectedId ?? undefined, { shouldValidate: true });
                    setAgenteIntegradorLabel(item?.nome ?? '');
                  }}
                  fetchPage={(params) => agenteIntegradorService.list(params)}
                  fetchById={(selectedId) => agenteIntegradorService.getById(selectedId)}
                  getOptionLabel={(a) => a.nome}
                  queryKey="agentes-select"
                  placeholder="Buscar agente…"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-6 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/estagios')}
            className="w-full sm:w-auto"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full sm:w-auto"
          >
            {isSubmitting ? 'Salvando...' : (isEditing ? 'Atualizar Estágio' : 'Criar Estágio')}
          </Button>
        </div>
      </form>
    </div>
  );
};
